import { Form, FormProps, Input, Modal, ModalProps } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useGetDetailCompany } from "../../../api/company";
import { IWalletBody } from "../../../api/wallet";
import { ICompany } from "../../../interfaces/companies";
import { ICurrencyToken } from "../../../interfaces/currencyToken";
import { INetwork } from "../../../interfaces/network";
import { ButtonListFooterModalSM } from "../../../pages/components/buttonsList";
import { confirmModalUnsavedChangesProps } from "../../../pages/components/constants";
import { addressWalletRules } from "../../../pages/payment-request/addCompany.constants";
import {
  mapCompanyItem,
  mapCurrencyToken,
  mapNetwork,
} from "../../../utils/edit.constants";
import { companySearchFieldNameFilter } from "../../../utils/form.constants";
import { useNotificationOkCancel } from "../../../utils/hooks/useNotificationOkCancel.hook";
import {
  AutoComplete,
  AutoCompleteCurrencyToken,
  AutoCompleteNetwork,
} from "../../inputs/autocomplete";
import { Option } from "../../inputs/autocomplete/autocompleteCurrencyToken";
import { DownOutlined } from "@ant-design/icons";

interface IProps {
  formProps: FormProps;
  modalProps: ModalProps;
  formIsLoading: boolean;
  formSubmit: (data: any) => void;
  closeModal: () => void;
  companyId?: number;
  userId?: number;
}

export const NewWalletModalCreate = ({
  modalProps,
  formProps,
  formIsLoading,
  formSubmit,
  closeModal,
  companyId,
  userId,
}: IProps) => {
  const isModalOpen = modalProps.open || false;
  const { data: companyDetail, refetch: refetchCompanyDetail } = useGetDetailCompany(
    companyId || null,
    isModalOpen && !!companyId
  );

  const [currencyData, setCurrencyData] = useState<ICurrencyToken>();
  const [networkData, setNetworkData] = useState<INetwork>();
  const [companyData, setCompanyData] = useState<ICompany>();

  const address = Form.useWatch("address", formProps?.form);

  const onSubmit = useCallback(
    (formValues: IWalletBody) => {
      const data = {
        companyId: companyId || companyData?.id,
        name: formValues?.name,
        assetConfigGroupId: Number(currencyData?.id) || null,
        networkId: Number(networkData?.id) || null,
        address: formValues.address || "",
        userId: userId || null,
      };
      formSubmit(data);
      formProps.form?.resetFields();
      closeModal();
    },
    [
      closeModal,
      companyId,
      companyData?.id,
      currencyData?.id,
      formProps.form,
      formSubmit,
      networkData?.id,
      userId,
    ]
  );

  const handleSubmit = useCallback(() => {
    formProps.form?.submit();
  }, [formProps.form]);

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: closeModal,
  });

  const changeCurrencyData = useCallback(
    (data: ICurrencyToken) => {
      if (data.id !== currencyData?.id) {
        setCurrencyData(data);
      }
    },
    [currencyData?.id]
  );

  const onSelectCurrency = useCallback(
    (_: ICurrencyToken, { data }: Option<ICurrencyToken>) => {
      return changeCurrencyData(data);
    },
    [changeCurrencyData]
  );

  const clearCurrencyData = useCallback(() => {
    setCurrencyData(undefined);
  }, []);

  const changeNetworkData = useCallback(
    (data: INetwork) => {
      if (data.id !== networkData?.id) {
        setNetworkData(data);
      }
    },
    [networkData?.id]
  );

  const onSelectNetwork = useCallback(
    (_: INetwork, { data }: Option<INetwork>) => {
      return changeNetworkData(data);
    },
    [changeNetworkData]
  );

  const clearNetworkData = useCallback(() => {
    setNetworkData(undefined);
  }, []);

  const changeCompanyData = useCallback(
    (data: ICompany) => {
      if (data.id !== companyData?.id) {
        setCompanyData(data);
      }
    },
    [companyData?.id]
  );

  const onSelectCompany = useCallback(
    (_: ICompany, { data }: Option<ICompany>) => {
      return changeCompanyData(data);
    },
    [changeCompanyData]
  );

  const clearCompanyData = useCallback(() => {
    setCompanyData(undefined);
  }, []);

  const onModalCancel = useCallback(() => {
    const formIsNotEmpty = Object.values(formProps.form?.getFieldsValue()).some(
      (value) => value !== undefined
    );
    if (formIsNotEmpty) {
      openNotification();
    } else {
      closeModal();
    }
  }, [closeModal, formProps.form, openNotification]);

  const afterClose = useCallback(() => {
    formProps.form?.resetFields();
    setCurrencyData(undefined);
    setNetworkData(undefined);
    if (!companyId) {
      setCompanyData(undefined);
    }
  }, [formProps, companyId]);

  // Reload company data when modal opens
  useEffect(() => {
    if (isModalOpen && companyId) {
      refetchCompanyDetail();
    }
  }, [isModalOpen, companyId, refetchCompanyDetail]);

  // Reset form and state when modal opens (if no companyId)
  useEffect(() => {
    if (isModalOpen && !companyId) {
      setCurrencyData(undefined);
      setNetworkData(undefined);
      setCompanyData(undefined);
      formProps?.form?.resetFields();
    }
  }, [isModalOpen, companyId, formProps?.form]);

  // Set company data when loaded
  useEffect(() => {
    if (companyId && companyDetail?.data) {
      const company = companyDetail.data;
      setCompanyData(company);
      formProps?.form?.setFieldsValue({
        company: company.fullName || company.name,
      });
    } else if (!companyId && isModalOpen) {
      // Reset company field if no companyId and modal is open
      formProps?.form?.setFieldsValue({
        company: undefined,
      });
    }
  }, [companyId, companyDetail, formProps?.form, isModalOpen]);

  useEffect(() => {
    if (currencyData && networkData && address && address.length >= 4) {
      const last4 = address.slice(-4);
      formProps?.form?.setFieldsValue({
        name: `${currencyData?.symbol}-${networkData?.nativeCurrencySymbol}-${last4}`,
      });
    } else {
      formProps?.form?.setFieldsValue({ name: "" });
    }
  }, [address, formProps?.form, currencyData, networkData]);

  return (
    <>
      <Modal
        {...modalProps}
        title="Add new wallet"
        className="smartModalSM"
        footer={ButtonListFooterModalSM(
          handleSubmit,
          onModalCancel,
          formIsLoading
        )}
        afterClose={afterClose}
        onCancel={onModalCancel}
      >
        {contextHolder}
        <Form
          {...formProps}
          layout="vertical"
          onFinish={onSubmit}
          disabled={formIsLoading}
        >
          <Form.Item
            name="currency"
            rules={[
              {
                required: true,
                message: `Please select currency`,
                validateTrigger: "onSubmit",
              },
            ]}
          >
            <AutoCompleteCurrencyToken
              resource={`api/v1/asset-config-groups/crypto`}
              mapItem={mapCurrencyToken}
              onSelect={onSelectCurrency}
              allowClear
              refetchOnEmptyValue
              onClear={clearCurrencyData}
              params={networkData?.id}
            >
              <Input placeholder="Currency" size="large" suffix={<DownOutlined />} />
            </AutoCompleteCurrencyToken>
          </Form.Item>
          <Form.Item
            name="network"
            rules={[
              {
                required: true,
                message: `Please select network`,
                validateTrigger: "onSubmit",
              },
            ]}
          >
            <AutoCompleteNetwork
              resource="api/v1/networks"
              mapItem={mapNetwork}
              onSelect={onSelectNetwork}
              allowClear
              refetchOnEmptyValue
              onClear={clearNetworkData}
              params={currencyData?.id}
            >
              <Input placeholder="Network" size="large" suffix={<DownOutlined />} />
            </AutoCompleteNetwork>
          </Form.Item>
          <Form.Item name="address" rules={addressWalletRules}>
            <Input
              placeholder="Address Wallet"
              size="large"
              style={{
                height: "35px",
                fontSize: "14px",
              }}
            />
          </Form.Item>
          <Form.Item
            name="name"
            rules={[
              {
                required: true,
                message: "This field is required",
                whitespace: true,
              },
              {
                min: 3,
                message: "Minimum 3 characters",
                validateTrigger: "onChange",
              },
              {
                max: 120,
                message: "Maximum 120 characters",
                validateTrigger: "onChange",
              },
            ]}
          >
            <Input
              disabled
              placeholder="Wallet name"
              size="large"
              style={{
                height: "35px",
                fontSize: "14px",
              }}
            />
          </Form.Item>
          <Form.Item
            name="company"
            rules={[
              {
                required: true,
                message: `Please select company`,
                validateTrigger: "onSubmit",
              },
            ]}
          >
            <AutoComplete
              resource={`api/v1/companies`}
              mapItem={mapCompanyItem}
              onSelect={onSelectCompany}
              additionalFilters={[companySearchFieldNameFilter]}
              allowClear={!companyId}
              refetchOnEmptyValue
              onClear={clearCompanyData}
              disabled={!!companyId}
            >
              <Input placeholder="Provider" size="large" suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
